import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

interface CategoryBadgeProps {
  category: string | null | undefined;
  size?: 'sm' | 'md';
}

const CATEGORY_STYLES: Record<string, { bg: string; text: string; border: string }> = {
  lending: { bg: 'bg-blue-500/10', text: 'text-blue-600', border: 'border-blue-500/20' },
  dex: { bg: 'bg-green-500/10', text: 'text-green-600', border: 'border-green-500/20' },
  dexes: { bg: 'bg-green-500/10', text: 'text-green-600', border: 'border-green-500/20' },
  derivatives: { bg: 'bg-purple-500/10', text: 'text-purple-600', border: 'border-purple-500/20' },
  staking: { bg: 'bg-yellow-500/10', text: 'text-yellow-600', border: 'border-yellow-500/20' },
  'liquid staking': { bg: 'bg-yellow-500/10', text: 'text-yellow-600', border: 'border-yellow-500/20' },
  lst: { bg: 'bg-yellow-500/10', text: 'text-yellow-600', border: 'border-yellow-500/20' },
  bridge: { bg: 'bg-orange-500/10', text: 'text-orange-600', border: 'border-orange-500/20' },
  cdp: { bg: 'bg-pink-500/10', text: 'text-pink-600', border: 'border-pink-500/20' },
  yield: { bg: 'bg-teal-500/10', text: 'text-teal-600', border: 'border-teal-500/20' },
  'yield aggregator': { bg: 'bg-teal-500/10', text: 'text-teal-600', border: 'border-teal-500/20' },
  chain: { bg: 'bg-indigo-500/10', text: 'text-indigo-600', border: 'border-indigo-500/20' },
  rwa: { bg: 'bg-amber-500/10', text: 'text-amber-600', border: 'border-amber-500/20' },
  nft: { bg: 'bg-rose-500/10', text: 'text-rose-600', border: 'border-rose-500/20' },
  gaming: { bg: 'bg-violet-500/10', text: 'text-violet-600', border: 'border-violet-500/20' },
  oracle: { bg: 'bg-cyan-500/10', text: 'text-cyan-600', border: 'border-cyan-500/20' },
  insurance: { bg: 'bg-emerald-500/10', text: 'text-emerald-600', border: 'border-emerald-500/20' },
  launchpad: { bg: 'bg-fuchsia-500/10', text: 'text-fuchsia-600', border: 'border-fuchsia-500/20' },
};

const getDisplayName = (category: string): string => {
  const normalizedCategory = category.toLowerCase();
  const nameMap: Record<string, string> = {
    lending: 'Lending',
    dex: 'DEX',
    dexes: 'DEX',
    derivatives: 'Derivatives',
    staking: 'Staking',
    'liquid staking': 'LST',
    lst: 'LST',
    bridge: 'Bridge',
    cdp: 'CDP',
    yield: 'Yield',
    'yield aggregator': 'Yield',
    chain: 'Chain',
    rwa: 'RWA',
    nft: 'NFT',
    gaming: 'Gaming',
    oracle: 'Oracle',
    insurance: 'Insurance',
    launchpad: 'Launchpad',
  };
  return nameMap[normalizedCategory] || category;
};

export const CategoryBadge = ({ category, size = 'sm' }: CategoryBadgeProps) => {
  if (!category || category.trim() === '') return null;

  const normalizedCategory = category.toLowerCase();
  const styles = CATEGORY_STYLES[normalizedCategory] || {
    bg: 'bg-muted',
    text: 'text-muted-foreground',
    border: 'border-muted',
  };

  return (
    <Badge
      variant="outline"
      className={cn(
        styles.bg,
        styles.text,
        styles.border,
        size === 'sm' ? 'text-[10px] px-1.5 py-0 h-4' : 'text-xs px-2 py-0.5'
      )}
    >
      {getDisplayName(category)}
    </Badge>
  );
};

// Export categories for filter dropdown
export const DEFI_CATEGORIES = [
  { value: 'all', label: 'Todas Categorias' },
  { value: 'lending', label: 'Lending' },
  { value: 'dex', label: 'DEX' },
  { value: 'derivatives', label: 'Derivatives' },
  { value: 'liquid staking', label: 'Staking / LST' },
  { value: 'bridge', label: 'Bridge' },
  { value: 'cdp', label: 'CDP' },
  { value: 'yield', label: 'Yield' },
  { value: 'chain', label: 'Chain' },
  { value: 'rwa', label: 'RWA' },
];
